import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workAuthService } from '@/services/workAuthService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  ExternalLink
} from 'lucide-react';

interface SmartsheetColumn {
  id: number;
  title: string;
  type: string;
  index: number;
  primary?: boolean;
}

interface SmartsheetRow {
  id: number;
  rowNumber: number;
  data: Record<string, any>;
}

interface SmartsheetPreviewData {
  sheetId: string;
  sheetName: string;
  totalRows: number;
  totalColumns: number;
  columns: SmartsheetColumn[];
  rows: SmartsheetRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Key columns to display prominently
const KEY_COLUMNS = [
  'Employee ID (Dayforce)',
  'Employee Name',
  "employee's email",
  'Document Expiration Date / Section 1 Work Authoriz',
  "employee's manager name",
  "manager's email",
  'Citizenship Status',
  'Document Type'
];

const WorkAuthSmartsheetPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<SmartsheetPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const fetchData = async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await workAuthService.getSmartsheetPreview(pageNum, pageSize);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Failed to load Smartsheet data');
      }
    } catch (err) {
      console.error('Error fetching Smartsheet preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Smartsheet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [page]);

  const handleRefresh = () => {
    fetchData(page);
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && data.pagination.hasMore) {
      setPage(page + 1);
    }
  };

  // Get display columns - prioritize key columns, then fill with remaining
  const getDisplayColumns = (): SmartsheetColumn[] => {
    if (!data) return [];

    const keyColumns: SmartsheetColumn[] = [];
    const otherColumns: SmartsheetColumn[] = [];

    for (const col of data.columns) {
      if (KEY_COLUMNS.includes(col.title)) {
        keyColumns.push(col);
      } else {
        otherColumns.push(col);
      }
    }

    // Sort key columns by their order in KEY_COLUMNS array
    keyColumns.sort((a, b) => {
      return KEY_COLUMNS.indexOf(a.title) - KEY_COLUMNS.indexOf(b.title);
    });

    return [...keyColumns, ...otherColumns];
  };

  const formatCellValue = (value: any, columnTitle: string): string => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Truncate long email addresses for display
    if (columnTitle.toLowerCase().includes('email') && typeof value === 'string' && value.length > 30) {
      return value.substring(0, 27) + '...';
    }

    // Truncate long names
    if (columnTitle.toLowerCase().includes('name') && typeof value === 'string' && value.length > 25) {
      return value.substring(0, 22) + '...';
    }

    return String(value);
  };

  const getColumnWidth = (columnTitle: string): string => {
    if (columnTitle.toLowerCase().includes('email')) return 'min-w-[200px]';
    if (columnTitle.toLowerCase().includes('name')) return 'min-w-[150px]';
    if (columnTitle.toLowerCase().includes('date')) return 'min-w-[120px]';
    if (columnTitle.toLowerCase().includes('id')) return 'min-w-[100px]';
    return 'min-w-[120px]';
  };

  if (loading && !data) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/hr/work-auth/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {data && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://app.smartsheet.com/sheets/${data.sheetId}`, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in Smartsheet
            </Button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      {data && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle className="text-xl">{data.sheetName}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {data.totalRows.toLocaleString()} rows • {data.totalColumns} columns
                  </p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Sheet ID: {data.sheetId}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto scrollbar-visible">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium text-muted-foreground w-12">
                        #
                      </th>
                      {getDisplayColumns().slice(0, 8).map((column) => (
                        <th
                          key={column.id}
                          className={`px-3 py-3 text-left font-medium text-muted-foreground ${getColumnWidth(column.title)}`}
                          title={column.title}
                        >
                          <div className="truncate max-w-[180px]">
                            {column.title}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.rows.map((row, rowIndex) => (
                      <tr
                        key={row.id}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.rowNumber}
                        </td>
                        {getDisplayColumns().slice(0, 8).map((column) => (
                          <td
                            key={`${row.id}-${column.id}`}
                            className="px-3 py-2"
                            title={String(row.data[column.title] || '')}
                          >
                            <div className="truncate max-w-[180px]">
                              {formatCellValue(row.data[column.title], column.title)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, data.totalRows)} of {data.totalRows.toLocaleString()} rows
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={page === 1 || loading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page} of {data.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!data.pagination.hasMore || loading}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkAuthSmartsheetPage;
